/**
 * CatalogRow — one catalogue entry as a single list row (brief-26).
 *
 * Density over presentation: mark, title, one-line truncated description, badge,
 * and the same actions the card carries. Everything is on ONE line — the
 * description truncates with an ellipsis rather than wrapping, so ten services
 * scan at a glance and every row has identical height.
 *
 * decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench applies here
 * exactly as it does to the card: the row carries Manage and Pin, nothing else.
 * Deploy and Edit description moved to the App Operations Board. A row is a
 * denser rendering of a tile, never a second place where the action set is
 * decided — if these two lists ever disagree, the row is wrong.
 *
 * The card's other rules carry over unchanged: no commerce CTA, no detail modal
 * and no dimming on the management plane
 * (convention-admin-catalogue-no-commerce-cta).
 */

import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import PinToggle from './PinToggle';

interface CatalogRowProps {
  tile: TileConfig;
  badgeClass: string;
  badgeLabel: string;
  pinned: boolean;
  onTogglePin: () => void;
  /** auth/surfaceAccess canManageApp() for THIS tile. Absent, not denied. */
  canManage?: boolean;
  onNavigate?: (path: Route) => void;
}

export default function CatalogRow({
  tile, badgeClass, badgeLabel, pinned, onTogglePin, canManage = false, onNavigate,
}: CatalogRowProps) {
  return (
    <li className={`dp-row${pinned ? ' dp-row--pinned' : ''}`}>
      <span className="dp-row__pin">
        <PinToggle pinned={pinned} title={tile.title} onToggle={onTogglePin} />
      </span>

      <span className="dp-row__mark" aria-hidden="true">{tile.mark}</span>

      <span className="dp-row__text">
        <span className="dp-row__title">{tile.title}</span>
        <span className="dp-row__desc">{tile.description}</span>
      </span>

      <span className={`dp-badge dp-badge--inline ${badgeClass}`}>{badgeLabel}</span>

      <span className="dp-row__actions">
        {canManage && (
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => onNavigate?.(`/apps/${tile.id}/operations`)}
          >
            {t('tile.manage')}
          </button>
        )}
      </span>
    </li>
  );
}
